import React, { useEffect, useState } from 'react';
import styles from './style.module.scss';

const IGNORED_KEYS = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'created_at', 'updated_at', 'po']);

const deepSearchSingleWord = (obj, word) => {
    if (typeof obj === 'string' || typeof obj === 'number') {
        return obj.toString().toLowerCase().includes(word);
    }

    if (Array.isArray(obj)) {
        return obj.some(item => deepSearchSingleWord(item, word));
    }

    if (typeof obj === 'object' && obj !== null) {
        return Object.entries(obj).some(([key, value]) => {
            if (IGNORED_KEYS.has(key)) {
                return false;
            }
            return deepSearchSingleWord(value, word);
        });
    }

    return false;
};

const deepSearch = (obj, searchTerm) => {
    if (!searchTerm) return true;
    
    // Parse search terms supporting quoted phrases
    const regex = /"([^"]+)"|'([^']+)'|(\S+)/g;
    const words = [];
    let match;
    while ((match = regex.exec(searchTerm)) !== null) {
        const token = match[1] || match[2] || match[3];
        if (token) {
            words.push(token.toLowerCase());
        }
    }

    if (words.length === 0) return true;
    return words.every(word => deepSearchSingleWord(obj, word));
};

function InventoySearchBar({ searchKey = 'name', suggestionsSource = [], onSearchChange }) {
    const [query, setQuery] = useState('');
    const [sortOption, setSortOption] = useState('created_desc');

    const sortOptions = [
        { label: 'Date Created: New to Old', value: 'created_desc' },
        { label: 'Date Created: Old to New', value: 'created_asc' },
        { label: 'Name: A to Z', value: 'name_asc' },
        { label: 'Name: Z to A', value: 'name_desc' }
    ];

    // Helper to get nested value from object by "dot" path
    const getNestedValue = (obj, path) => {
        try {
            return path.split('.').reduce((acc, key) => acc && acc[key], obj) || '';
        } catch {
            return '';
        }
    };

    useEffect(() => {
        let filtered = [...(suggestionsSource || [])];

        if (query) {
            filtered = filtered.filter((item) => deepSearch(item, query));
        }

        // ✅ Sorting logic remains the same
        switch (sortOption) {
            case 'created_desc':
                filtered.sort(
                    (a, b) =>
                        new Date(getNestedValue(b, 'po.created_at')) -
                        new Date(getNestedValue(a, 'po.created_at'))
                );
                break;
            case 'created_asc':
                filtered.sort(
                    (a, b) =>
                        new Date(getNestedValue(a, 'po.created_at')) -
                        new Date(getNestedValue(b, 'po.created_at'))
                );
                break;
            case 'name_asc':
                filtered.sort((a, b) => {
                    const nameA = getNestedValue(a, 'product.name') || '';
                    const nameB = getNestedValue(b, 'product.name') || '';
                    return nameA.localeCompare(nameB);
                });
                break;
            case 'name_desc':
                filtered.sort((a, b) => {
                    const nameA = getNestedValue(a, 'product.name') || '';
                    const nameB = getNestedValue(b, 'product.name') || '';
                    return nameB.localeCompare(nameA);
                });
                break;
        }

        // Send updated filtered list to parent
        onSearchChange(filtered);
    }, [query, suggestionsSource, sortOption, searchKey, onSearchChange]);

    return (
        <div className={styles.search_view}>
            <div className={styles.search_input_wrapper}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={styles.search_input}
                />
                <span className={styles.search_icon_button} type="button">
                    🔍
                </span>
            </div>

            <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className={styles.sort_dropdown}
            >
                {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default InventoySearchBar;
